import React, { useState, useEffect, useContext } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Avatar, 
  Menu, 
  MenuItem, 
  IconButton, 
  Tooltip,
  Badge,
  Divider,
  Slide,
  useScrollTrigger,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  Grid,
  Card,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  RocketLaunch, 
  Logout, 
  Settings, 
  Dashboard, 
  CreditCard,
  Notifications,
  Science,
  ExpandMore,
  ExpandLess,
  LightMode,
  DarkMode,
  Star,
  Timer,
  Upgrade,
  Warning,
  CheckCircle,
  Cancel,
  Close,
  Payment,
  Lock,
  WorkspacePremium
} from '@mui/icons-material';
import { styled, alpha, useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { UIContext } from '../../contexts/UIContext';
import axios from 'axios';

// Enhanced utility functions with trial support
const getCreditColor = (credits) => {
  if (credits > 10) return '#4caf50';
  if (credits > 5) return '#ff9800';
  if (credits > 0) return '#f44336';
  return '#9e9e9e';
};

const getCreditBackgroundColor = (credits) => {
  if (credits > 10) return '#4caf50';
  if (credits > 5) return '#ff9800';
  if (credits > 0) return '#f44336';
  return '#bdbdbd';
};

const getTrialStatusColor = (trialStatus) => {
  switch (trialStatus) {
    case 'active': return '#4caf50';
    case 'expired': return '#f44336';
    case 'ending_soon': return '#ff9800';
    default: return '#9e9e9e';
  }
};

const getAccessLevelColor = (accessLevel) => {
  switch (accessLevel) {
    case 'subscribed': return '#4caf50';
    case 'trial': return '#2196f3';
    case 'trial_expired': return '#f44336';
    case 'free': return '#ff9800';
    default: return '#9e9e9e';
  }
};

const getAccessLevelText = (accessLevel) => {
  switch (accessLevel) {
    case 'subscribed': return 'PRO';
    case 'trial': return 'TRIAL';
    case 'trial_expired': return 'TRIAL EXPIRED';
    case 'free': return 'FREE';
    default: return 'FREE';
  }
};

const getCreditAnimation = (credits, isTrialActive = false) => {
  if (credits === 0) {
    return {
      animation: '0.8s infinite blinkAnimation, 2s infinite shakeAnimation',
    };
  }
  if (credits <= 2) {
    return {
      animation: '2s infinite pulseAnimation, 1.5s infinite glowAnimation',
    };
  }
  if (isTrialActive) {
    return {
      animation: '3s infinite gentlePulseAnimation',
    };
  }
  return {};
};

// Animation styles
const creditAnimationStyles = {
  '@keyframes blinkAnimation': {
    '0%': { opacity: 0.3 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.3 }
  },
  '@keyframes shakeAnimation': {
    '0%, 100%': { transform: 'translateX(0)' },
    '25%': { transform: 'translateX(-2px)' },
    '75%': { transform: 'translateX(2px)' }
  },
  '@keyframes pulseAnimation': {
    '0%': { opacity: 0.8 },
    '50%': { opacity: 1 },
    '100%': { opacity: 0.8 }
  },
  '@keyframes glowAnimation': {
    '0%': { boxShadow: '0 0 5px rgba(244, 67, 54, 0.5)' },
    '50%': { boxShadow: '0 0 20px rgba(244, 67, 54, 0.8)' },
    '100%': { boxShadow: '0 0 5px rgba(244, 67, 54, 0.5)' }
  },
  '@keyframes gentlePulseAnimation': {
    '0%, 100%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.02)' }
  }
};

// Styled components
const HolographicButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.8)} 0%, ${alpha(theme.palette.secondary.main, 0.6)} 100%)`,
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  boxShadow: `0 8px 32px 0 ${alpha(theme.palette.primary.main, 0.3)}`,
  color: 'white',
  borderRadius: '12px',
  padding: '8px 20px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 40px 0 ${alpha(theme.palette.primary.main, 0.5)}`,
    background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.secondary.main, 0.7)} 100%)`,
  },
}));

// Functional components for Chips
const TrialBadge = ({ label, status, ...props }) => (
  <Chip
    label={label}
    sx={{
      background: `linear-gradient(45deg, ${getTrialStatusColor(status)} 30%, ${alpha(getTrialStatusColor(status), 0.7)} 90%)`,
      color: 'white',
      fontWeight: 'bold',
      fontSize: '0.7rem',
      height: '24px',
      '& .MuiChip-label': {
        padding: '0 8px',
      },
    }}
    {...props}
  />
);

const AccessLevelChip = ({ label, level, ...props }) => (
  <Chip
    label={label}
    sx={{
      background: `linear-gradient(45deg, ${getAccessLevelColor(level)} 30%, ${alpha(getAccessLevelColor(level), 0.7)} 90%)`,
      color: 'white',
      fontWeight: 'bold',
      fontSize: '0.75rem',
      height: '28px',
    }}
    {...props}
  />
);

const FloatingAvatar = styled(Avatar)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  border: `2px solid ${theme.palette.primary.main}`,
  boxShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.5)}`,
  '&:hover': {
    transform: 'scale(1.1) rotate(10deg)',
    boxShadow: `0 0 30px ${alpha(theme.palette.secondary.main, 0.7)}`,
  },
}));

const GlowingBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

// Subscription Plans Configuration
const SUBSCRIPTION_PLANS = {
  monthly: {
    id: 'monthly',
    name: 'Monthly Pro',
    price: 9999,
    inrPrice: 9999,
    displayPrice: 9999,
    currency: 'INR',
    currencySymbol: '₹',
    interval: 'month',
    creditsPerMonth: 3,
    totalCredits: 3,
    features: [
      'Full access to all services',
      '3 credits added monthly',
      'Priority email support',
      'Advanced analytics',
      'Scenario storage',
      'PDF export capabilities',
      'Cancel anytime'
    ],
    popular: true
  },
  yearly: {
    id: 'yearly',
    name: 'Yearly Pro',
    price: 99999,
    inrPrice: 99999,
    displayPrice: 99999,
    currency: 'INR',
    currencySymbol: '₹',
    interval: 'year',
    creditsPerMonth: 3,
    totalCredits: 36,
    savings: 18,
    features: [
      'Everything in Monthly Pro',
      'Save ₹200 annually',
      'Priority phone support',
      'Dedicated account manager',
      'Custom reporting',
      'Early access to new features',
      'Best value'
    ],
    popular: false
  }
};

export default function FuturisticNavbar() {
  const navigate = useNavigate();
  const theme = useTheme();
  
  const { 
    user, 
    isAuthenticated, 
    credits, 
    logout, 
    token,
    forceRefreshUser,
    initializing,
    isSubscribed,
    isTrialActive,
    isTrialExpired,
    trialDaysRemaining,
    accessLevel,
    forceSubscriptionUpdate,
    purchaseSubscription,
    verifySubscriptionPayment,
    updateSubscriptionStatus,
    trackUserAction
  } = useContext(AuthContext);
  
  const { toggleTheme, themeMode } = useContext(UIContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [hovering, setHovering] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [trialProgress, setTrialProgress] = useState(0);
  
  // Subscription Management State
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentStep, setPaymentStep] = useState(0);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [subscriptionSnackbar, setSubscriptionSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });

  const isMenuOpen = Boolean(anchorEl);
  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 10 });

  // Calculate trial progress and determine trial status
  useEffect(() => {
    if (isTrialActive && trialDaysRemaining !== undefined && trialDaysRemaining !== null) {
      const totalTrialDays = 14;
      const daysUsed = totalTrialDays - trialDaysRemaining;
      const progress = (daysUsed / totalTrialDays) * 100;
      setTrialProgress(Math.min(progress, 100));
    }
  }, [isTrialActive, trialDaysRemaining]);

  // Enhanced user data initialization
  useEffect(() => {
    const initializeUserData = async () => {
      if (initializing) return;
      
      const storedToken = localStorage.getItem('authToken') || localStorage.getItem('token');
      
      if (storedToken && (!user || !user._id)) {
        setLocalLoading(true);
        try {
          await forceRefreshUser();
          await forceSubscriptionUpdate();
        } catch (error) {
          console.warn('Navbar: Failed to refresh user data:', error);
        } finally {
          setLocalLoading(false);
        }
      }
    };

    initializeUserData();
  }, [initializing, user, forceRefreshUser, forceSubscriptionUpdate]);

  // Enhanced data fetching with trial notifications
  useEffect(() => {
    const fetchAdditionalData = async () => {
      if (!isAuthenticated || !user?._id || !token) return;
      
      try {
        const [notificationsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/notifications`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        ]);
        
        let enhancedNotifications = notificationsRes.data?.notifications || notificationsRes.data || [];
        
        // Add trial-related notifications
        if (isTrialActive) {
          if (trialDaysRemaining <= 3 && trialDaysRemaining > 0) {
            enhancedNotifications.unshift({
              id: 'trial-ending-soon',
              type: 'warning',
              title: `Trial Expires in ${trialDaysRemaining} day${trialDaysRemaining !== 1 ? 's' : ''}`,
              message: 'Upgrade to keep your premium features',
              timestamp: new Date().toISOString(),
              action: { type: 'upgrade', path: '/subscription' }
            });
          } else if (trialDaysRemaining === 1) {
            enhancedNotifications.unshift({
              id: 'trial-last-day',
              type: 'error',
              title: 'Trial Ends Tomorrow!',
              message: 'Upgrade now to avoid losing premium features',
              timestamp: new Date().toISOString(),
              action: { type: 'upgrade', path: '/subscription' }
            });
          }
        }
        
        if (isTrialExpired) {
          enhancedNotifications.unshift({
            id: 'trial-expired',
            type: 'error',
            title: 'Trial Period Ended',
            message: 'Upgrade now to restore premium features',
            timestamp: new Date().toISOString(),
            action: { type: 'upgrade', path: '/subscription' }
          });
        }
        
        setNotifications(enhancedNotifications);
      } catch (error) {
        console.error('Failed to fetch additional user data:', error);
      }
    };

    fetchAdditionalData();
  }, [isAuthenticated, user, token, isTrialActive, isTrialExpired, trialDaysRemaining]);

  // Load Razorpay script for subscription management
  useEffect(() => {
    const loadRazorpay = () => {
      if (window.Razorpay) {
        setRazorpayLoaded(true);
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        setRazorpayLoaded(true);
        console.log('✅ Razorpay SDK loaded successfully');
      };
      script.onerror = () => {
        console.error('❌ Failed to load Razorpay SDK');
        setRazorpayLoaded(false);
      };
      document.head.appendChild(script);
    };

    if (subscriptionDialogOpen && !window.Razorpay) {
      loadRazorpay();
    }
  }, [subscriptionDialogOpen]);

  // Subscription Management Functions
  const handleOpenSubscriptionDialog = () => {
    setSubscriptionDialogOpen(true);
    setPaymentStep(0);
    setSelectedPlan(null);
    
    if (trackUserAction) {
      trackUserAction('subscription_dialog_opened', {
        source: 'navbar',
        userStatus: getUserStatusDisplay().level
      });
    }
  };

  const handleCloseSubscriptionDialog = () => {
    setSubscriptionDialogOpen(false);
    setPaymentStep(0);
    setSelectedPlan(null);
    setProcessingPayment(false);
  };

  const handlePlanSelect = (planId) => {
    const plan = SUBSCRIPTION_PLANS[planId];
    setSelectedPlan(plan);
    setPaymentStep(1);
    
    if (trackUserAction) {
      trackUserAction('plan_selected', {
        planId: plan.id,
        planName: plan.name,
        price: plan.price,
        interval: plan.interval,
        source: 'navbar'
      });
    }
  };

  const initiatePayment = async () => {
    if (!selectedPlan || !razorpayLoaded) {
      setSubscriptionSnackbar({
        open: true,
        message: 'Please select a plan and wait for payment system to load',
        severity: 'warning'
      });
      return;
    }

    setProcessingPayment(true);

    try {
      const orderData = await purchaseSubscription(selectedPlan.id);
      
      if (!orderData.order || !orderData.order.id) {
        throw new Error('Invalid order response from server');
      }

      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.order.amount,
        currency: orderData.order.currency || 'INR',
        name: "Quantum ROI Analyzer Pro",
        description: `${selectedPlan.name} Subscription`,
        order_id: orderData.order.id,
        handler: async (response) => {
          await handlePaymentSuccess(response);
        },
        prefill: {
          name: user?.name || user?.email?.split('@')[0] || 'Customer',
          email: user?.email,
          contact: user?.phone || ''
        },
        theme: { color: "#2c5aa0" },
        modal: {
          ondismiss: () => {
            setProcessingPayment(false);
            if (trackUserAction) {
              trackUserAction('payment_modal_dismissed', {
                planId: selectedPlan.id,
                step: 'payment_modal'
              });
            }
          }
        },
        notes: {
          type: 'subscription',
          plan_id: selectedPlan.id,
          user_id: user._id,
          credits: selectedPlan.totalCredits
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
      if (trackUserAction) {
        trackUserAction('payment_initiated', {
          planId: selectedPlan.id,
          orderId: orderData.order.id,
          amount: orderData.order.amount,
          source: 'navbar'
        });
      }

    } catch (error) {
      console.error('Payment initiation failed:', error);
      setSubscriptionSnackbar({
        open: true,
        message: 'Failed to initiate payment. Please try again.',
        severity: 'error'
      });
      setProcessingPayment(false);
    }
  };

  const handlePaymentSuccess = async (paymentResponse) => {
    try {
      setProcessingPayment(true);
      
      const verificationData = {
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        planId: selectedPlan.id
      };

      const result = await verifySubscriptionPayment(verificationData);
      
      if (result.success) {
        if (updateSubscriptionStatus) {
          updateSubscriptionStatus({
            planId: selectedPlan.id,
            planName: selectedPlan.name,
            startDate: new Date().toISOString(),
            status: 'active',
            creditsAdded: selectedPlan.totalCredits
          });
        }

        await forceRefreshUser();
        await forceSubscriptionUpdate();
        
        setPaymentStep(2);
        
        setSubscriptionSnackbar({
          open: true,
          message: `Successfully subscribed to ${selectedPlan.name}!`,
          severity: 'success'
        });

        if (trackUserAction) {
          trackUserAction('subscription_activated', {
            planId: selectedPlan.id,
            paymentId: paymentResponse.razorpay_payment_id,
            amount: selectedPlan.price,
            interval: selectedPlan.interval,
            source: 'navbar'
          });
        }

      } else {
        throw new Error(result.error || 'Payment verification failed');
      }
    } catch (error) {
      console.error('Payment verification failed:', error);
      setSubscriptionSnackbar({
        open: true,
        message: 'Payment verification failed. Please contact support.',
        severity: 'error'
      });
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMoreAnchorEl(null);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    handleMobileMenuClose();
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login');
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMoreAnchorEl(event.currentTarget);
  };

  const getTrialStatusText = () => {
    if (isTrialActive) {
      if (trialDaysRemaining === 1) return '1 day left';
      return `${trialDaysRemaining} days left`;
    }
    if (isTrialExpired) {
      return 'Trial expired';
    }
    return null;
  };

  const getTrialStatus = () => {
    if (isTrialActive) {
      if (trialDaysRemaining <= 3) return 'ending_soon';
      return 'active';
    }
    if (isTrialExpired) return 'expired';
    return null;
  };

  const getUserStatusDisplay = () => {
    if (isSubscribed) {
      return {
        label: 'PRO',
        level: 'subscribed',
        showChip: true,
        icon: <Star sx={{ fontSize: 14 }} />
      };
    }
    
    if (isTrialActive) {
      return {
        label: 'TRIAL',
        level: 'trial',
        showChip: true
      };
    }
    
    if (isTrialExpired) {
      return {
        label: 'TRIAL EXPIRED',
        level: 'trial_expired',
        showChip: true
      };
    }
    
    return {
      label: 'FREE',
      level: 'free',
      showChip: true
    };
  };

  // Show loading state while AuthContext is initializing
  if (initializing || localLoading) {
    return (
      <AppBar position="fixed" sx={{ background: 'transparent', backdropFilter: 'blur(10px)' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <RocketLaunch sx={{ fontSize: 32, mr: 1, color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              QUANTUM ANALYZER
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        </Toolbar>
      </AppBar>
    );
  }

  const userStatus = getUserStatusDisplay();

  // Subscription Dialog Component
  const SubscriptionDialog = () => (
    <Dialog 
      open={subscriptionDialogOpen} 
      onClose={handleCloseSubscriptionDialog} 
      maxWidth="md" 
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h5" fontWeight="bold">
            <RocketLaunch sx={{ mr: 1, verticalAlign: 'middle' }} />
            Upgrade to Pro
          </Typography>
          <Button onClick={handleCloseSubscriptionDialog} size="small" disabled={processingPayment}>
            <Close />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={paymentStep} sx={{ mb: 4 }}>
          <Step><StepLabel>Choose Plan</StepLabel></Step>
          <Step><StepLabel>Payment</StepLabel></Step>
          <Step><StepLabel>Confirmation</StepLabel></Step>
        </Stepper>

        {paymentStep === 0 && (
          <Grid container spacing={3}>
            {Object.values(SUBSCRIPTION_PLANS).map((plan) => (
              <Grid item xs={12} md={6} key={plan.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    border: selectedPlan?.id === plan.id ? '2px solid' : '1px solid',
                    borderColor: selectedPlan?.id === plan.id ? 'primary.main' : 'divider',
                    transform: selectedPlan?.id === plan.id ? 'scale(1.02)' : 'scale(1)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {plan.popular && (
                    <Chip
                      label="MOST POPULAR"
                      color="primary"
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: -10,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        fontWeight: 'bold'
                      }}
                    />
                  )}
                  
                  <CardContent sx={{ pt: plan.popular ? 4 : 2 }}>
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                      <Typography variant="h5" gutterBottom fontWeight="bold">
                        {plan.name}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', mb: 1 }}>
                        <Typography variant="h4" color="primary" fontWeight="bold">
                          {plan.currencySymbol}{plan.price.toLocaleString('en-IN')}
                        </Typography>
                        <Typography variant="h6" color="text.secondary" sx={{ ml: 1 }}>
                          /{plan.interval}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {plan.totalCredits} credits included
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    
                    <List dense>
                      {plan.features.map((feature, index) => (
                        <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircle color="success" sx={{ fontSize: 20 }} />
                          </ListItemIcon>
                          <ListItemText primary={feature} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      fullWidth
                      variant={selectedPlan?.id === plan.id ? "contained" : "outlined"}
                      size="large"
                      onClick={() => handlePlanSelect(plan.id)}
                      disabled={processingPayment}
                      startIcon={selectedPlan?.id === plan.id ? <CheckCircle /> : <WorkspacePremium />}
                    >
                      {selectedPlan?.id === plan.id ? 'Selected' : 'Choose Plan'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {paymentStep === 1 && selectedPlan && (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64, mx: 'auto', mb: 2 }}>
              <Payment />
            </Avatar>
            
            <Typography variant="h5" gutterBottom fontWeight="bold">
              Complete Your Payment
            </Typography>
            
            <Typography variant="body1" color="text.secondary" gutterBottom>
              You're subscribing to: <strong>{selectedPlan.name}</strong>
            </Typography>
            
            <Box sx={{ my: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
              <Typography variant="h4" color="primary" fontWeight="bold">
                {selectedPlan.currencySymbol}{selectedPlan.price.toLocaleString('en-IN')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedPlan.interval === 'month' ? 'per month' : 'per year'}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                {selectedPlan.totalCredits} credits included
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="large"
              fullWidth
              onClick={initiatePayment}
              disabled={!razorpayLoaded || processingPayment}
              startIcon={processingPayment ? null : <Lock />}
              sx={{ py: 1.5, mb: 2 }}
            >
              {processingPayment ? 'Processing...' : `Pay ${selectedPlan.currencySymbol}${selectedPlan.price.toLocaleString('en-IN')} Now`}
            </Button>
            
            <Typography variant="caption" color="text.secondary" display="block">
              🔒 Secure payment powered by Razorpay
            </Typography>
          </Box>
        )}

        {paymentStep === 2 && (
          <Box sx={{ textAlign: 'center', p: 3 }}>
            <Avatar sx={{ bgcolor: 'success.main', width: 64, height: 64, mx: 'auto', mb: 2 }}>
              <CheckCircle />
            </Avatar>
            
            <Typography variant="h4" gutterBottom fontWeight="bold" color="success.main">
              Welcome to {selectedPlan?.name}!
            </Typography>
            
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Your subscription has been activated successfully
            </Typography>
            
            <Box sx={{ my: 3, p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
              <Typography variant="body1" fontWeight="bold">
                🎉 {selectedPlan?.totalCredits} credits added to your account
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                You now have access to all premium features
              </Typography>
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={handleCloseSubscriptionDialog}
              startIcon={<RocketLaunch />}
            >
              Start Using Premium Features
            </Button>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        {paymentStep === 1 && (
          <Button onClick={() => setPaymentStep(0)} disabled={processingPayment}>
            Back to Plans
          </Button>
        )}
        {paymentStep === 2 && (
          <Button onClick={handleCloseSubscriptionDialog} variant="contained">
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );

  // Enhanced menu with comprehensive trial and subscription management
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      open={isMenuOpen}
      onClose={handleMenuClose}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'visible',
          filter: 'drop-shadow(0px 5px 15px rgba(0,0,0,0.3))',
          mt: 1.5,
          minWidth: 320,
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '12px',
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
    >
      {/* Enhanced User Info Section with Trial Status */}
      <MenuItem sx={{ py: 1.5, flexDirection: 'column', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
          <Avatar 
            src={user?.avatar} 
            sx={{ 
              width: 36, 
              height: 36, 
              mr: 2,
              border: `2px solid ${theme.palette.primary.main}` 
            }}
          >
            {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">
              {user?.name || 'User'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          
          {userStatus.showChip && (
            <AccessLevelChip 
              label={userStatus.label} 
              level={userStatus.level}
              size="small"
              icon={userStatus.icon}
            />
          )}
        </Box>

        {/* Subscription Status Display */}
        {isSubscribed && (
          <Box sx={{ width: '100%', mt: 1, p: 1, bgcolor: 'success.50', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircle sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                <Typography variant="caption" fontWeight="bold" color="success.main">
                  Pro Subscription Active
                </Typography>
              </Box>
              <Star sx={{ fontSize: 14, color: '#FFD700' }} />
            </Box>
          </Box>
        )}

        {/* Trial Status Display */}
        {(isTrialActive || isTrialExpired) && (
          <Box sx={{ width: '100%', mt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isTrialActive ? (
                  <Timer sx={{ fontSize: 16, mr: 0.5, color: getTrialStatusColor(getTrialStatus()) }} />
                ) : (
                  <Cancel sx={{ fontSize: 16, mr: 0.5, color: getTrialStatusColor('expired') }} />
                )}
                <Typography variant="caption" fontWeight="bold">
                  {isTrialActive ? 'Trial Period' : 'Trial Expired'}
                </Typography>
              </Box>
              <Typography 
                variant="caption" 
                fontWeight="bold"
                color={getTrialStatusColor(getTrialStatus())}
              >
                {getTrialStatusText()}
              </Typography>
            </Box>
            
            {isTrialActive && (
              <LinearProgress 
                variant="determinate" 
                value={trialProgress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.primary.main, 0.2),
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: getTrialStatusColor(getTrialStatus()),
                    borderRadius: 3,
                  }
                }}
              />
            )}
            
            {isTrialActive && trialDaysRemaining <= 3 && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                <Warning sx={{ fontSize: 12, mr: 0.5 }} />
                Upgrade to continue premium access
              </Typography>
            )}

            {isTrialExpired && (
              <Typography variant="caption" color="error.main" sx={{ mt: 0.5, display: 'block' }}>
                <Warning sx={{ fontSize: 12, mr: 0.5 }} />
                Upgrade to restore premium features
              </Typography>
            )}
          </Box>
        )}

        {/* Credits Display */}
        <Box sx={{ width: '100%', mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" fontWeight="bold">
              Available Credits:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CreditCard sx={{ fontSize: 14, mr: 0.5, color: getCreditColor(credits) }} />
              <Typography 
                variant="caption" 
                fontWeight="bold"
                color={getCreditColor(credits)}
              >
                {credits} {isTrialActive && 'Trial '}Credits
              </Typography>
              {isTrialActive && <Star sx={{ fontSize: 12, ml: 0.5, color: '#FFD700' }} />}
            </Box>
          </Box>
        </Box>

        {/* Upgrade Button for Non-Subscribed Users */}
        {!isSubscribed && (
          <Button
            fullWidth
            variant="contained"
            startIcon={<Upgrade />}
            onClick={handleOpenSubscriptionDialog}
            sx={{
              mt: 1.5,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
              fontWeight: 'bold',
              py: 1,
              borderRadius: 2,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}
          >
            {isTrialActive 
              ? `Upgrade Now - ${trialDaysRemaining} Day${trialDaysRemaining !== 1 ? 's' : ''} Left` 
              : isTrialExpired 
                ? 'Restore Premium Access' 
                : 'Upgrade to Premium'
            }
          </Button>
        )}
      </MenuItem>

      <Divider sx={{ my: 0.5, opacity: 0.5 }} />
      
      <MenuItem onClick={() => { handleMenuClose(); navigate('/dashboard'); }}>
        <Dashboard sx={{ mr: 2, color: 'primary.main' }} />
        Dashboard
      </MenuItem>
      
      <MenuItem onClick={() => { handleMenuClose(); navigate('/settings'); }}>
        <Settings sx={{ mr: 2, color: 'primary.main' }} />
        Settings
      </MenuItem>

      {/* Subscription Management */}
      <MenuItem onClick={() => { handleMenuClose(); navigate('/subscription'); }}>
        <CreditCard sx={{ mr: 2, color: isSubscribed ? 'success.main' : 'primary.main' }} />
        {isSubscribed ? 'Manage Subscription' : 'View Plans & Pricing'}
      </MenuItem>

      {/* Credit Purchase */}
      <MenuItem onClick={() => { handleMenuClose(); navigate('/scenarios?tab=credits'); }}>
        <CreditCard sx={{ mr: 2, color: getCreditColor(credits) }} />
        Buy More Credits
      </MenuItem>
      
      <MenuItem onClick={toggleTheme}>
        {themeMode === 'dark' ? (
          <>
            <LightMode sx={{ mr: 2, color: 'warning.main' }} />
            Light Mode
          </>
        ) : (
          <>
            <DarkMode sx={{ mr: 2, color: 'primary.main' }} />
            Dark Mode
          </>
        )}
      </MenuItem>
      
      <Divider sx={{ my: 0.5, opacity: 0.5 }} />
      
      <MenuItem onClick={handleLogout}>
        <Logout sx={{ mr: 2, color: 'error.main' }} />
        Logout
      </MenuItem>
    </Menu>
  );

  // Enhanced mobile menu with trial support
  const renderMobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
      PaperProps={{
        sx: {
          width: '80vw',
          maxWidth: 320,
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.default, 0.9)} 0%, ${alpha(theme.palette.background.paper, 0.9)} 100%)`,
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '16px',
          overflow: 'hidden',
        },
      }}
    >
      <MenuItem sx={{ py: 2, background: alpha(theme.palette.primary.main, 0.1), flexDirection: 'column', alignItems: 'flex-start' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', mb: 1 }}>
          <Avatar 
            src={user?.avatar} 
            sx={{ 
              width: 40, 
              height: 40, 
              mr: 2,
              border: `2px solid ${theme.palette.primary.main}` 
            }}
          >
            {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold">{user?.name || 'User'}</Typography>
            <Typography variant="caption" color="text.secondary">
              {credits} Credits • {userStatus.label}
            </Typography>
          </Box>
        </Box>

        {/* Mobile Trial Status */}
        {(isTrialActive || isTrialExpired) && (
          <Box sx={{ width: '100%', mt: 1 }}>
            <TrialBadge 
              label={getTrialStatusText()} 
              status={getTrialStatus()}
              size="small"
            />
            {isTrialActive && trialDaysRemaining <= 3 && (
              <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                <Warning sx={{ fontSize: 12, mr: 0.5 }} />
                Upgrade recommended
              </Typography>
            )}
          </Box>
        )}
      </MenuItem>
      
      <Divider sx={{ my: 0.5, opacity: 0.5 }} />
      
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/notifications'); }}>
        <Badge badgeContent={notifications.length} color="error" sx={{ mr: 2 }}>
          <Notifications />
        </Badge>
        Notifications
      </MenuItem>
      
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/dashboard'); }}>
        <Dashboard sx={{ mr: 2 }} />
        Dashboard
      </MenuItem>
      
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/subscription'); }}>
        <CreditCard sx={{ mr: 2 }} />
        {isSubscribed ? 'Subscription' : 'Upgrade'}
      </MenuItem>

      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/credits'); }}>
        <CreditCard sx={{ mr: 2 }} />
        Buy Credits
      </MenuItem>
      
      <MenuItem onClick={() => { handleMobileMenuClose(); navigate('/settings'); }}>
        <Settings sx={{ mr: 2 }} />
        Settings
      </MenuItem>
      
      <MenuItem onClick={toggleTheme}>
        {themeMode === 'dark' ? (
          <>
            <LightMode sx={{ mr: 2 }} />
            Light Mode
          </>
        ) : (
          <>
            <DarkMode sx={{ mr: 2 }} />
            Dark Mode
          </>
        )}
      </MenuItem>
      
      <Divider sx={{ my: 0.5, opacity: 0.5 }} />
      
      <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
        <Logout sx={{ mr: 2 }} />
        Logout
      </MenuItem>
    </Menu>
  );

  return (
    <>
      <Slide appear={false} direction="down" in={!trigger}>
        <AppBar 
          position="fixed" 
          sx={{ 
            background: trigger 
              ? alpha(theme.palette.background.default, 0.8) 
              : 'transparent',
            backdropFilter: trigger ? 'blur(20px)' : 'none',
            boxShadow: 'none',
            borderBottom: trigger 
              ? `1px solid ${alpha(theme.palette.divider, 0.1)}` 
              : 'none',
            transition: 'all 0.5s ease',
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between', py: trigger ? 0.5 : 1.5 }}>
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': { transform: 'scale(1.02)' },
              }}
              onClick={() => navigate('/')}
              onMouseEnter={() => setHovering(true)}
              onMouseLeave={() => setHovering(false)}
            >
              <motion.div
                animate={{ rotate: hovering ? 10 : 0, scale: hovering ? 1.1 : 1 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <RocketLaunch sx={{ 
                  fontSize: 32, 
                  mr: 1, 
                  color: theme.palette.primary.main,
                  filter: `drop-shadow(0 0 8px ${alpha(theme.palette.primary.main, 0.5)})`,
                }} />
              </motion.div>
              
              <Typography
                variant="h6"
                noWrap
                sx={{ 
                  mr: 2,
                  fontWeight: 'bold',
                  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: `0 0 10px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              >
                QUANTUM ANALYZER
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {isAuthenticated ? (
                <>
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                    {/* Trial Status Badge for Desktop */}
                    {(isTrialActive || isTrialExpired) && (
                      <TrialBadge 
                        label={getTrialStatusText()} 
                        status={getTrialStatus()}
                        size="small"
                      />
                    )}

                    {/* Access Level Badge */}
                    {userStatus.showChip && (
                      <AccessLevelChip 
                        label={userStatus.label} 
                        level={userStatus.level}
                        icon={userStatus.icon}
                      />
                    )}
                    
                    <HolographicButton onClick={() => navigate('/dashboard')} startIcon={<Dashboard />}>
                      Dashboard
                    </HolographicButton>
                    
                    <Tooltip title="Notifications">
                      <IconButton onClick={() => navigate('/notifications')}>
                        <GlowingBadge badgeContent={notifications.length} color="error">
                          <Notifications />
                        </GlowingBadge>
                      </IconButton>
                    </Tooltip>
                    
                    {/* Enhanced Credits Display with Trial Context */}
                    <Tooltip title={
                      isSubscribed ? 
                        `${credits} pro credits available` :
                      isTrialActive ? 
                        `${credits} trial credits - ${trialDaysRemaining} days remaining` :
                        `${credits} credits available - Click to manage`
                    }>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          px: 2,
                          py: 1,
                          borderRadius: '12px',
                          background: alpha(getCreditBackgroundColor(credits), 0.2),
                          border: `2px solid ${alpha(getCreditColor(credits), 0.3)}`,
                          mr: 1,
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          ...getCreditAnimation(credits, isTrialActive),
                          ...creditAnimationStyles,
                          '&:hover': {
                            transform: 'scale(1.05)',
                            background: alpha(getCreditBackgroundColor(credits), 0.3),
                            border: `2px solid ${alpha(getCreditColor(credits), 0.5)}`,
                          }
                        }}
                        onClick={() => navigate('/roi-analyzer?tab=credits')}
                      >
                        <CreditCard sx={{ 
                          fontSize: 20, 
                          mr: 1, 
                          color: getCreditColor(credits),
                        }} />
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            fontWeight: 'bold',
                            color: getCreditColor(credits),
                            textShadow: credits <= 2 ? '0 0 10px rgba(255,255,255,0.8)' : 'none',
                          }}
                        >
                          {credits}
                          {isTrialActive && <Star sx={{ fontSize: 14, ml: 0.5, color: '#FFD700' }} />}
                          {isSubscribed && <Star sx={{ fontSize: 14, ml: 0.5, color: '#4caf50' }} />}
                        </Typography>
                      </Box>
                    </Tooltip>
                    
                    <Tooltip title="Account menu">
                      <IconButton onClick={handleProfileMenuOpen} sx={{ ml: 1 }}>
                        <FloatingAvatar src={user?.avatar}>
                          {user?.name?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                        </FloatingAvatar>
                      </IconButton>
                    </Tooltip>
                  </Box>
                  
                  <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                    <IconButton onClick={handleMobileMenuOpen} color="inherit">
                      {isMobileMenuOpen ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                </>
              ) : (
                <>
                  <HolographicButton onClick={() => navigate('/login')} sx={{ mr: 1 }}>
                    Sign In
                  </HolographicButton>
                  <HolographicButton onClick={() => navigate('/register')}>
                    Start Free Trial
                  </HolographicButton>
                </>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      </Slide>

      {renderMenu}
      {renderMobileMenu}
      
      {/* Subscription Dialog */}
      <SubscriptionDialog />

      {/* Subscription Snackbar */}
      <Snackbar
        open={subscriptionSnackbar.open}
        autoHideDuration={6000}
        onClose={() => setSubscriptionSnackbar({ ...subscriptionSnackbar, open: false })}
      >
        <Alert 
          severity={subscriptionSnackbar.severity} 
          onClose={() => setSubscriptionSnackbar({ ...subscriptionSnackbar, open: false })}
        >
          {subscriptionSnackbar.message}
        </Alert>
      </Snackbar>

      <AnimatePresence>
        {isAuthenticated && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: theme.zIndex.speedDial,
            }}
          >
            <Tooltip title="Quick Actions">
              <Button
                onClick={() => navigate('/quick-actions')}
                sx={{
                  minWidth: 'auto',
                  width: 56,
                  height: 56,
                  borderRadius: '50%',
                  boxShadow: `0 8px 25px -5px ${alpha(theme.palette.primary.main, 0.3)}`,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  '&:hover': { transform: 'scale(1.1)' },
                }}
              >
                <Science sx={{ fontSize: 28 }} />
              </Button>
            </Tooltip>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}